'use client';

import { usePlannerDayCounsels } from '@/entities/visit-counsel';
import { useOrderMapMarkers } from '@/features/order/order-map-markers';
import { useAppTheme } from '@/shared/theme';
import type { DepartureLocationInfo, OrderStatus } from '@/entities/order/model/order.types';
import type { PlannerDayCounsel } from '@/entities/visit-counsel/model/usePlannerDayCounsels';
import type { Marker, Arrow } from '@/shared/ui/display/MapboxMap';

export interface UseCounselMapDataParams {
  visitCounselId: string | null;
  assignedDepartureLocation: DepartureLocationInfo | null | undefined;
  fallbackOrder?: { id: string; misoOrderId: string } | null;
  orderStatus?: OrderStatus['type'];
  /** 스토리/테스트용: API 대신 이 데이터 사용 */
  counselsOverride?: PlannerDayCounsel[];
  /** true면 마커 배지를 상담순서 대신 상담시간(13, 15½)으로 표시 */
  viewByTime?: boolean;
  /** true면 마커 번호를 플래너별로 1,2,3... 매김 */
  numberingPerPlanner?: boolean;
  /** 설정 시 해당 플래너의 이동동선만 화살표로 표시 */
  selectedPlannerIdForArrows?: string | null;
  /** 팝업 링크 URL 오버라이드 (visitCounselId → URL) */
  markerLinkResolver?: (visitCounselId: string) => string | undefined;
  /** 플래너별 핀·이동동선 색 (상담 지도) */
  plannerColorById?: Record<string, string>;
}

export interface UseCounselMapDataResult {
  markers: Marker[];
  arrows: Arrow[];
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * CounselMap 데이터 조회 훅
 * - counselsOverride 없음: usePlannerDayCounsels로 API 조회
 * - counselsOverride 있음: API 스킵, 오버라이드 데이터 사용
 */
export function useCounselMapData({
  visitCounselId,
  assignedDepartureLocation,
  fallbackOrder = null,
  orderStatus,
  counselsOverride,
  viewByTime = false,
  numberingPerPlanner = false,
  selectedPlannerIdForArrows = null,
  markerLinkResolver,
  plannerColorById,
}: UseCounselMapDataParams): UseCounselMapDataResult {
  const { mode: themeMode } = useAppTheme();
  const {
    counsels: fetchedCounsels,
    isLoading,
    error,
  } = usePlannerDayCounsels(
    counselsOverride === undefined ? visitCounselId : null,
    assignedDepartureLocation ?? null
  );

  const counsels = counselsOverride ?? fetchedCounsels;
  const { markers, arrows } = useOrderMapMarkers(
    counsels,
    assignedDepartureLocation,
    themeMode,
    fallbackOrder,
    orderStatus,
    viewByTime,
    numberingPerPlanner,
    selectedPlannerIdForArrows,
    markerLinkResolver,
    plannerColorById
  );

  return {
    markers,
    arrows,
    isLoading: counselsOverride !== undefined ? false : isLoading,
    error: counselsOverride !== undefined ? undefined : error,
  };
}
